import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  IconButton,
  useMediaQuery,
  useTheme,
  Tooltip,
  Box,
  Typography,
  Stack,
  Badge,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Assignment,
  AttachMoney,
  Business,
  CalendarToday,
  Check,
  Close,
  Description,
  Edit,
  FileCopy,
  LocationOn,
  People,
  Print,
} from '@mui/icons-material';

import OverviewTab from './tabs/overview_tab';
import ComplianceTab from './tabs/compliance_tab';
import OfficersTab from './tabs/officers_tab';
import FinancialsTab from './tabs/financials_tab';
import DocumentsTab from './tabs/documents_tab';

import { getCompanyDetailsAsString } from '../../../utilities/generate_clipboard_content';
import { formatDate } from '../../../utilities/formatter';

const CopyButton = ({ content }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Tooltip
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      arrow
      TransitionProps={{ timeout: 300 }}
    >
      <IconButton
        onClick={handleCopy}
        color={copied ? 'success' : 'default'}
        sx={{
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transform: 'scale(1.1)',
          },
        }}
      >
        {copied ? (
          <Check fontSize="small" sx={{ color: '#4caf50' }} />
        ) : (
          <FileCopy fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'suspended':
      return 'error';
    default:
      return 'default';
  }
};

const CompanyDetailDialog = ({ company, open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!company) return null;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const tabComponents = [
    {
      label: 'Overview',
      icon: <Business fontSize="small" />,
      component: <OverviewTab company={company} />,
    },
    {
      label: 'Compliance',
      icon: <Assignment fontSize="small" />,
      component: <ComplianceTab company={company} />,
    },
    {
      label: 'Officers',
      icon: <People fontSize="small" />,
      component: <OfficersTab company={company} />,
    },
    {
      label: 'Financials',
      icon: <AttachMoney fontSize="small" />,
      component: <FinancialsTab company={company} />,
    },
    {
      label: 'Documents',
      icon: <Description fontSize="small" />,
      component: <DocumentsTab company={company} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: { xs: 'none', sm: '0px 4px 20px rgba(0, 0, 0, 0.1)' },
          height: { xs: '100%', sm: '90vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: { xs: 1, sm: 2 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Business sx={{ mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {company.CompanyName}
            </Typography>
            <Typography variant="subtitle1">
              {company.CompanyNumber} • {company.CompanyType}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Tooltip title="Close">
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        <Box
          sx={{
            p: 3,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: 32,
                }}
              >
                {company.CompanyName.charAt(0)}
              </Avatar>
            </Badge>

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {company.CompanyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {company.SICCodes?.join(' • ')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Chip
                  label={company.Status}
                  size="small"
                  color={getStatusColor(company.Status)}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                  }}
                />
                <Chip
                  icon={<CalendarToday fontSize="small" />}
                  label={`Incorporated: ${formatDate(company.IncorporationDate)}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<LocationOn fontSize="small" />}
                  label={company.RegisteredAddress.city}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<AttachMoney fontSize="small" />}
                  label={`£${company.ShareStructure?.statementOfCapital?.totalAmount} Capital`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Edit">
                <IconButton color="primary" disabled>
                  <Edit />
                </IconButton>
              </Tooltip>
              <CopyButton content={getCompanyDetailsAsString(company)} />
              <Tooltip title="Print">
                <IconButton color="primary" disabled>
                  <Print />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
        {/* Tabs */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minHeight: { xs: 48, sm: 64 } }}
          >
            {tabComponents.map((tab, index) => (
              <Tab
                key={index}
                label={isMobile ? null : tab.label}
                icon={tab.icon}
                iconPosition={isMobile ? 'top' : 'start'}
                sx={{ minHeight: { xs: 48, sm: 64 }, px: { xs: 1, sm: 2 } }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: '60vh' }}>
          {tabComponents[tabValue].component}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
          fullWidth={isMobile}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyDetailDialog;
